import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { mainMenu, type NavMenu } from '../data/navigation';
import { easeOutStrong } from '../utils/motion';
const dropdownMotion = {
  initial: {
    opacity: 0,
    y: -6
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -6
  },
  transition: {
    duration: 0.18,
    ease: easeOutStrong
  }
};
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal';
const topLinkClass = `text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150 whitespace-nowrap rounded-sm ${focusRing}`;
const mobileLinkClass = `block px-3 py-3 text-base font-medium text-ink-dark hover:bg-surface-muted rounded-md ${focusRing}`;
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileGroup, setOpenMobileGroup] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenDropdown(null);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);
  const closeMobile = () => {
    setMobileMenuOpen(false);
    setOpenMobileGroup(null);
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-[padding,background-color,box-shadow] duration-200 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-line py-3' : 'bg-white border-b border-line py-4'}`} data-id="element-1503" data-mp="1a683h0dwsb6e">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1504" data-mp="1k21rbfx2vr3h">
        <div className="flex justify-between items-center" data-id="element-1505" data-mp="ugs862b52rc1">
          <a href="/" className={`flex items-center rounded-sm ${focusRing}`} aria-label="SyNeural home" data-id="element-1506" data-mp="ph5tdofnx314">
            <img src="https://cdn.magicpatterns.com/uploads/vBy52qowPNFNY53vkUADE7/SyNeural-logo-no-tagline-half-size.svg" alt="SyNeural" className={`w-auto transition-[height] duration-200 ease-out ${isScrolled ? 'h-8' : 'h-10'}`} data-id="element-1507" data-mp="146br17yuql6w" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main" data-id="element-1508" data-mp="186lxsmugxfmt">
            <ul className="flex items-center gap-8" data-id="element-1509" data-mp="1n4xu2sdximdg">
              {mainMenu.map(menu => <DesktopMenuItem key={menu.label} menu={menu} isOpen={openDropdown === menu.label} onOpen={() => setOpenDropdown(menu.label)} onClose={() => setOpenDropdown(null)} onToggle={() => setOpenDropdown(openDropdown === menu.label ? null : menu.label)} data-id="element-1510" data-mp="1i5bewdejiw20" />)}
            </ul>
            <div className="w-px h-5 bg-line" aria-hidden="true" data-id="element-1511" data-mp="24cozyqnom60c"></div>
            <a href="/contact" className={`bg-navy hover:bg-navy-light text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-150 shadow-sm whitespace-nowrap ${focusRing}`} data-id="element-1512" data-mp="1ppm2gjzczxd0">
              Contact
            </a>
          </nav>

          <button type="button" className={`lg:hidden text-ink-dark p-2 rounded-md ${focusRing}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" aria-expanded={mobileMenuOpen} data-id="element-1513" data-mp="1kqivuv3qdj2x">
            {mobileMenuOpen ? <X size={24} data-id="element-1514" data-mp="314iv03ur54p4" /> : <Menu size={24} data-id="element-1515" data-mp="313zm1cn2xqn8" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      <AnimatePresence data-id="element-1516" data-mp="362ax1qp42771">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.22,
        ease: easeOutStrong
      }} className="lg:hidden bg-white border-b border-line overflow-hidden" data-id="element-1517" data-mp="1614a1zwdnmyq">
            <nav className="px-4 pt-2 pb-6 max-h-[80vh] overflow-y-auto" aria-label="Mobile" data-id="element-1518" data-mp="s1uxda7drazi">
              <ul className="divide-y divide-line" data-id="element-1519" data-mp="n2s1ogdaih47">
                {mainMenu.map(menu => {
              if (!menu.items) {
                return <li key={menu.label} data-id="element-1520" data-mp="i9p4ly9b0pj9">
                        <a href={menu.href} className={mobileLinkClass} onClick={closeMobile} data-id="element-1521" data-mp="dbnif4gl0a3k">
                          {menu.label}
                        </a>
                      </li>;
              }
              const groupId = `mobile-${menu.label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
              const expanded = openMobileGroup === menu.label;
              return <li key={menu.label} data-id="element-1522" data-mp="2az7x7zn9mfov">
                      <button type="button" aria-expanded={expanded} aria-controls={groupId} onClick={() => setOpenMobileGroup(expanded ? null : menu.label)} className={`flex w-full items-center justify-between px-3 py-3 text-base font-medium text-ink-dark rounded-md hover:bg-surface-muted ${focusRing}`} data-id="element-1523" data-mp="2fz417yvyq74f">
                        {menu.label}
                        <ChevronDown size={18} aria-hidden="true" className={`text-ink-muted transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} data-id="element-1524" data-mp="1x6i65gsvqkkw" />
                      </button>
                      {expanded && <ul id={groupId} className="pb-2 pl-3" data-id="element-1525" data-mp="1dk3r3ywd92ex">
                          {menu.items.map(item => <li key={`${menu.label}-${item.label}`} data-id="element-1526" data-mp="2guhocsfnh01x">
                              <a href={item.href} onClick={closeMobile} className={`block px-3 py-2.5 text-[15px] rounded-md hover:bg-surface-muted ${focusRing} ${item.href === menu.href ? 'font-semibold text-navy' : 'text-ink-dark'}`} data-id="element-1527" data-mp="2bwpsjnbvwtsg">
                                {item.label}
                              </a>
                            </li>)}
                        </ul>}
                    </li>;
            })}
              </ul>
              <div className="pt-5 px-3" data-id="element-1528" data-mp="3ic8pf2yioeda">
                <a href="/contact" onClick={closeMobile} className="block w-full text-center bg-navy text-white font-medium px-5 py-3 rounded-md shadow-sm" data-id="element-1529" data-mp="3iaxi4t5v4ohf">
                  Contact
                </a>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}
type DesktopMenuItemProps = {
  menu: NavMenu;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
};
function DesktopMenuItem({
  menu,
  isOpen,
  onOpen,
  onClose,
  onToggle
}: DesktopMenuItemProps) {
  if (!menu.items) {
    return <li data-id="element-1530" data-mp="demmkahzh7o2">
        <a href={menu.href} className={topLinkClass} data-id="element-1531" data-mp="df5xh797uvsl">
          {menu.label}
        </a>
      </li>;
  }
  const menuId = `menu-${menu.label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return <li className="relative" onMouseEnter={onOpen} onMouseLeave={onClose} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onClose();
  }} data-id="element-1532" data-mp="3ob9nygbno51">
      <button type="button" onClick={onToggle} className={`${topLinkClass} flex items-center gap-1 py-2`} aria-expanded={isOpen} aria-controls={menuId} data-id="element-1533" data-mp="w65pyifx6nqm">
        {menu.label}
        <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1534" data-mp="2nfz53hfg7v1c" />
      </button>
      <AnimatePresence data-id="element-1535" data-mp="2ei5dgjsmppki">
        {isOpen && <motion.div {...dropdownMotion} id={menuId} className={`absolute top-full pt-2 ${menu.width ?? 'w-60'} ${menu.align === 'right' ? 'right-0' : 'left-0'}`} data-id="element-1536" data-mp="3b74na38v2zet">
            <ul className="bg-white rounded-lg shadow-lg border border-line py-2" data-id="element-1537" data-mp="3266ihiqlmnv6">
              {menu.items.map((item, index) => <li key={`${menu.label}-${item.label}`} className={index === 1 && item.href !== menu.href && menu.items?.[0].href === menu.href ? 'border-t border-line mt-1 pt-1' : ''} data-id="element-1538" data-mp="2x6k3p4v4gzk9" data-mpi={index}>
                  <a href={item.href} onClick={onClose} className={`block px-5 py-2.5 text-sm hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-none transition-colors duration-150 ${index === 0 && item.href === menu.href ? 'font-semibold text-navy' : 'text-ink-dark'}`} data-id="element-1539" data-mp="1lrx1j9oos1ed">
                    {item.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </li>;
}